import { useState, useMemo } from 'react';
import Select, { components } from 'react-select';
import countries from '../../data/countries';
import { Form, Row, Col, Badge, Button, InputGroup, Modal, Nav } from 'react-bootstrap';
import icSearch from '../../assets/img/ic-magnifier.svg';

const DiscoveryFilterBar = ({
    title = 'Companies',
    variant = 'default',
    searchQuery = '',
    setSearchQuery,
    selectedCountry = '',
    setSelectedCountry,
    selectedCity = '',
    setSelectedCity,
    selectedIndustry = '',
    setSelectedIndustry,
    selectedStudyDirection = '',
    setSelectedStudyDirection,
    selectedCompanySize = '',
    setSelectedCompanySize,
    selectedCompanyRating = '',
    setSelectedCompanyRating,
    items = [],
    industryNameByCode = new Map(),
    searchPlaceholder = 'Search...',
    countryPlaceholder = 'Filter by country...',
    cityPlaceholder = 'Filter by city...',
    industryPlaceholder = 'Filter by industry...',
    searchLabel = 'Search',
    countryLabel = 'Country',
    countryOptions = null,
    cityLabel = 'City',
    industryLabel = 'Industry',
    onSaveFilters,
    onClearFilters,
    selectedEnglishSpeaking = null,
    setSelectedEnglishSpeaking,
    selectedWorldTraveler = null,
    setSelectedWorldTraveler,
    selectedStudentJobAreas = null,
    setSelectedStudentJobAreas,
    aiIndustryGroupLabel = null,
    onClearAiIndustryGroup,
    savedFilterSets = [],
    selectedFilterSet = 'default',
    onFilterSetChange,
    onDeleteFilterSet,
    selectedFavorites = null,
    setSelectedFavorites,
    extraFilters = null,

    viewOptions = null,
    activeView = null,
    onViewChange = null,

    selectedContinentAllocation = '',
    setSelectedContinentAllocation,
    continentAllocationOptions = [],

    selectedLanguagesSpoken = [],
    setSelectedLanguagesSpoken,
    languagesSpokenOptions = [],
    showContinentAllocation = true,

    showCountryFilter = true,
    showCityFilter = true,
    cityField = 'city',
    allowedCities = null,
    mobileModalOnly = false,
    mobileModalOpen = false,
    onMobileModalClose,
    onMobileModalApply,
    mobileModalTitle = title,
    mobileApplyLabel = 'Apply filters',
    hideTypeToggleOnMobile = false,
    hideActiveFilterSummaryOnMobile = false,
    useFullScreenMobilePanel = false,
    mobileSavedFilterLabel = 'Saved filter',
    mobileSaveFilterLabel = 'Save current filters',
    compactSavedFilterOnMobile = false,
    hideSearchInMobileModal = false,
}) => {
    const hideSearchField = mobileModalOnly && hideSearchInMobileModal;

    const [showSaveFilterModal, setShowSaveFilterModal] = useState(false);
    const [filterSetName, setFilterSetName] = useState('');

    const [showDeleteFilterModal, setShowDeleteFilterModal] = useState(false);
    const [filterSetToDelete, setFilterSetToDelete] = useState(null);

    const isCountriesVariant = variant === 'countries';
    const isCommentsVariant = variant === 'comments';

    const countryNameByCode = useMemo(() => {
        return new Map(
            (countries || []).map((country) => [
                String(country.id || '').trim().toUpperCase(),
                country.name || String(country.id || '').trim().toUpperCase(),
            ])
        );
    }, []);

    const uniqueCountries = [
        ...new Set(
            items
                .map((item) => String(item.country || '').trim().toUpperCase())
                .filter(Boolean)
        )
    ]
        .map((countryCode) => ({
            value: countryCode,
            label: countryNameByCode.get(countryCode) || countryCode,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    const resolvedCountryOptions = Array.isArray(countryOptions)
        ? countryOptions
        : uniqueCountries;

    const uniqueCities = [
        ...new Set(
            items
                .filter((item) => !selectedCountry || item.country === selectedCountry)
                .map((item) => String(item[cityField] || '').trim())
                .filter(Boolean)
                .filter((city) => !allowedCities || allowedCities.has(city))
        )
    ].sort();

    const uniqueIndustries = [
        ...new Set(
            items.flatMap((item) =>
                Array.isArray(item.industries)
                    ? item.industries
                        .map((entry) => entry?.industry ? String(entry.industry) : '')
                        .filter(Boolean)
                    : []
            )
        )
    ]
        .map((code) => {
            const rawLabel = industryNameByCode.get(String(code)) || String(code);
            const cleanedLabel = String(rawLabel).trim();

            return {
                value: code,
                label: cleanedLabel,
            };
        })
        .filter((industry) => industry.label && !/^\d+(?:\.\d+)?$/.test(industry.label))
        .sort((a, b) => a.label.localeCompare(b.label));

    const clearFilters = () => {
        if (onClearFilters) {
            onClearFilters();
            return;
        }

        // Search now lives in the mobile toolbar, independent of the Filters drawer —
        // "Clear all" here must not wipe it. Desktop keeps clearing it since Search is
        // still part of the same filter bar there.
        if (!mobileModalOnly) {
            setSearchQuery?.('');
        }
        setSelectedCountry?.('');
        setSelectedCity?.('');
        setSelectedIndustry?.('');
        setSelectedStudyDirection?.('');
        setSelectedCompanySize?.('');
        setSelectedCompanyRating?.('');
        setSelectedEnglishSpeaking?.(null);
        setSelectedWorldTraveler?.(null);
        setSelectedStudentJobAreas?.(null);
        onClearAiIndustryGroup?.();
        setSelectedFavorites?.(null);
        setSelectedContinentAllocation?.('');
        setSelectedLanguagesSpoken?.([]);
    };

    const formatStudyDirection = (value) => {
        const str = String(value).trim();

        return str
            .replace(/^\d+(?:\.\d+)?\s*[-–—]?\s*/, '')
            .replace(/([.:])\s*\d+(?:\.\d+)?\s*[-–—]?\s*/g, '$1 ')
            .replace(/^\(\s*/, '')
            .replace(/\)\s*$/, '')
            .replace(/\s{2,}/g, ' ')
            .trim();
    };

    const uniqueStudyDirections = [
        ...new Set(
            items.flatMap((item) => {
                const rawStudyDirections = item.studyFields?.preferredStudyDirections;

                if (Array.isArray(rawStudyDirections)) {
                    return rawStudyDirections
                        .map((direction) => formatStudyDirection(direction))
                        .filter(Boolean);
                }

                if (typeof rawStudyDirections === 'string') {
                    return rawStudyDirections
                        .split(',')
                        .map((direction) => formatStudyDirection(direction))
                        .filter(Boolean);
                }

                return [];
            })
        )
    ].sort((a, b) => a.localeCompare(b));

    const companySizeOptions = [
        '1 - 10 employees',
        '11 - 50 employees',
        '51 - 200 employees',
        '201 - 500 employees',
        '501 - 1,000 employees',
        '1,001 - 5,000 employees',
        '5,001 - 10,000 employees',
        '10001 - 50000 employees',
        '50001 - 100000',
        'over 100000',
    ];

    const companyRatingOptions = [
        { value: '4.5', label: '4.5+' },
        { value: '4', label: '4.0+' },
        { value: '3.5', label: '3.5+' },
        { value: '3', label: '3.0+' },
    ];

    const filterSetOptions = [
        { value: 'default', label: 'Default filter set', isDefault: true },
        ...savedFilterSets.map((filterSet) => ({
            _id: filterSet._id,
            value: filterSet._id,
            name: filterSet.name,
            label: filterSet.name,
            isDefault: false,
        })),
    ];

    const selectedFilterSetOption =
        filterSetOptions.find((option) => option.value === selectedFilterSet) ||
        filterSetOptions[0];

    const FilterSetOption = (props) => {
        const { data } = props;

        return (
            <components.Option {...props}>
                <div className="d-flex align-items-center justify-content-between w-100">
                    <span>{data.label}</span>

                    {!data.isDefault && (
                        <button
                            type="button"
                            className="filter-set-delete-btn"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setFilterSetToDelete(data);
                                setShowDeleteFilterModal(true);
                            }}
                            aria-label={`Delete ${data.label} filter set`}
                        >
                            ×
                        </button>
                    )}
                </div>
            </components.Option>
        );
    };

    const FilterSetSingleValue = (props) => {
        return <components.SingleValue {...props}>{props.data.label}</components.SingleValue>;
    };

    const hasActiveFilters = Boolean(
        searchQuery ||
        selectedCountry ||
        selectedCity ||
        selectedIndustry ||
        selectedStudyDirection ||
        selectedCompanySize ||
        selectedCompanyRating ||
        selectedEnglishSpeaking !== null ||
        selectedWorldTraveler !== null ||
        selectedStudentJobAreas !== null ||
        Boolean(aiIndustryGroupLabel) ||
        selectedFavorites !== null ||
        selectedContinentAllocation ||
        (Array.isArray(selectedLanguagesSpoken) ? selectedLanguagesSpoken.length > 0 : Boolean(selectedLanguagesSpoken))
    );

    const showTypeToggle = !(mobileModalOnly && hideTypeToggleOnMobile);
    const showActiveFilterSummary = hasActiveFilters && !(mobileModalOnly && hideActiveFilterSummaryOnMobile);
    const showCompactSavedFilter = mobileModalOnly && compactSavedFilterOnMobile;

    const filterBarMarkup = (
            <div className="filter discovery-filter-bar">
                <div className="d-block">
                    {!isCommentsVariant && (
                        <Row className="mb-3 align-items-center discovery-filter-bar__context-row">
                            {showTypeToggle && (
                                <Col xs={12} md>
                                    {viewOptions ? (
                                        <Nav variant="pills" className="nav-pills-view-toggle">
                                            {viewOptions.map(({ value, label }) => (
                                                <Nav.Item key={value}>
                                                    <Nav.Link
                                                        active={activeView === value}
                                                        onClick={() => onViewChange?.(value)}
                                                    >
                                                        {label}
                                                    </Nav.Link>
                                                </Nav.Item>
                                            ))}
                                        </Nav>
                                    ) : (
                                        <h2 className="mb-0">{title}</h2>
                                    )}
                                </Col>
                            )}

                            <Col xs="auto">
                                <div className={`discovery-filter-bar__filter-set${showCompactSavedFilter ? ' discovery-filter-bar__filter-set--compact-mobile' : ''}`}>
                                    {showCompactSavedFilter && (
                                        <Form.Label className="discovery-filter-bar__filter-set-label">
                                            {mobileSavedFilterLabel}
                                        </Form.Label>
                                    )}

                                    <Select
                                        options={filterSetOptions}
                                        value={selectedFilterSetOption}
                                        onChange={(selectedOption) =>
                                            onFilterSetChange?.(selectedOption?.value || 'default')
                                        }
                                        isSearchable={false}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        className="react-select sm mt-4"
                                        classNamePrefix="react-select"
                                        components={{
                                            Option: FilterSetOption,
                                            SingleValue: FilterSetSingleValue,
                                        }}
                                        styles={{
                                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        }}
                                    />

                                    {showCompactSavedFilter && (
                                        <Button
                                            type="button"
                                            variant="link"
                                            className="discovery-filter-bar__save-current"
                                            onClick={() => setShowSaveFilterModal(true)}
                                        >
                                            {mobileSaveFilterLabel}
                                        </Button>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    )}
                    {isCommentsVariant ? (
                        <Row className="g-3 align-items-end">
                            <Col md={4}>
                                <Form.Group controlId="discoverySearch">
                                    <Form.Label>{searchLabel}</Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            type="text"
                                            placeholder={searchPlaceholder}
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery?.(e.target.value)}
                                        />
                                        <InputGroup.Text>
                                            <img src={icSearch} alt="Search" />
                                        </InputGroup.Text>
                                    </InputGroup>
                                </Form.Group>
                            </Col>

                            <Col md={3}>
                                <Form.Group controlId="discoveryCountry">
                                    <Form.Label>
                                        {countryLabel} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select
                                        value={selectedCountry}
                                        onChange={(e) => {
                                            setSelectedCountry?.(e.target.value);
                                            setSelectedCity?.('');
                                        }}
                                    >
                                        <option value="">{countryPlaceholder}</option>
                                        {uniqueCountries.map((country) => (
                                            <option key={country.value} value={country.value}>
                                                {country.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col md={3}>
                                <Form.Group controlId="discoveryCity">
                                    <Form.Label>
                                        {cityLabel} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select
                                        value={selectedCity}
                                        onChange={(e) => setSelectedCity?.(e.target.value)}
                                    >
                                        <option value="">{cityPlaceholder}</option>
                                        {uniqueCities.map((city, index) => (
                                            <option key={index} value={city}>
                                                {city}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            {!mobileModalOnly && (
                                <Col xs="auto" className="d-flex align-items-end discovery-filter-save-col">
                                    <Button
                                        variant="primary"
                                        className="btn-sm discovery-filter-save-btn"
                                        onClick={() => setShowSaveFilterModal(true)}
                                    >
                                        Save filters
                                    </Button>
                                </Col>
                            )}
                        </Row>
                    ) : isCountriesVariant ? (
                        <>
                        <Row className="align-items-center flex-nowrap">
                            {!hideSearchField && (
                                <Col md={3} lg={3}>
                                    <Form.Group controlId="discoverySearch">
                                        <Form.Label>{searchLabel}</Form.Label>
                                        <InputGroup>
                                            <Form.Control
                                                type="text"
                                                placeholder={searchPlaceholder}
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery?.(e.target.value)}
                                            />
                                            <InputGroup.Text>
                                                <img src={icSearch} alt="Search" />
                                            </InputGroup.Text>
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                            )}

                            {showCountryFilter && setSelectedCountry && (
                                <Col md={2} lg={2}>
                                    <Form.Group controlId="discoveryCountry">
                                        <Form.Label>{countryLabel}</Form.Label>
                                        <Form.Select
                                            value={selectedCountry}
                                            onChange={(e) => setSelectedCountry(e.target.value)}
                                        >
                                            <option value="">Please select</option>
                                            {resolvedCountryOptions.map((country) => (
                                                <option key={country.value} value={country.value}>
                                                    {country.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            )}

                            {showContinentAllocation && (
                                <Col md={2} lg={2}>
                                    <Form.Group controlId="discoveryContinentAllocation">
                                        <Form.Label>Continent allocation</Form.Label>
                                        <Form.Select
                                            value={selectedContinentAllocation}
                                            onChange={(e) => setSelectedContinentAllocation?.(e.target.value)}
                                        >
                                            <option value="">Please select</option>
                                            {continentAllocationOptions.map((option, index) => {
                                                const value = typeof option === 'string' ? option : option.value;
                                                const label = typeof option === 'string' ? option : option.label;

                                                return (
                                                    <option key={index} value={value}>
                                                        {label}
                                                    </option>
                                                );
                                            })}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            )}

                            <Col md={3} lg={3}>
                                <Form.Group controlId="discoveryLanguagesSpoken">
                                    <Form.Label>Languages spoken</Form.Label>
                                    <Select
                                        options={languagesSpokenOptions}
                                        placeholder="Please select"
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        styles={{
                                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),

                                            control: (base) => ({
                                                ...base,
                                                minHeight: '32px',
                                            }),

                                            dropdownIndicator: (base) => ({
                                                ...base,
                                                padding: '4px',
                                            }),

                                            valueContainer: (base) => ({
                                                ...base,
                                                padding: '2px 8px',
                                            }),
                                        }}
                                        isMulti
                                        isSearchable
                                        className="react-select"
                                        classNamePrefix="react-select"
                                        
                                        value={languagesSpokenOptions.filter((option) =>
                                            selectedLanguagesSpoken.includes(option.value)
                                        )}
                                        onChange={(selectedOptions) =>
                                            setSelectedLanguagesSpoken?.(
                                                Array.isArray(selectedOptions)
                                                    ? selectedOptions.map((option) => option.value)
                                                    : []
                                            )
                                        }
                                    />
                                </Form.Group>
                            </Col>

                            {!mobileModalOnly && (
                                <Col md={2} lg={2} className="d-flex">
                                    <Form.Check
                                        type="switch"
                                        className="filter-switch pt-4"
                                        id="favorites-switch"
                                        label="Saved to Favorites"
                                        checked={selectedFavorites === true}
                                        onChange={(e) =>
                                            setSelectedFavorites?.(e.target.checked ? true : null)
                                        }
                                    />
                                </Col>
                            )}
                            {!mobileModalOnly && (
                                <Col xs="auto" className="d-flex align-items-end ms-auto pt-4 discovery-filter-save-col">
                                    <Button
                                        variant="primary"
                                        className="btn-sm discovery-filter-save-btn"
                                        onClick={() => setShowSaveFilterModal(true)}
                                    >
                                        Save filters
                                    </Button>
                                </Col>
                            )}
                        </Row>
                        {mobileModalOnly && (
                            <Row className="mt-3 discovery-filter-switch-row-group">
                                <Col xs={12} className="discovery-filter-switch-col">
                                    <div className="d-flex flex-wrap gap-3 justify-content-start discovery-filter-switches">
                                        <label className="discovery-filter-mobile-switch-row" htmlFor="favorites-switch">
                                            <span>Saved to Favorites</span>
                                            <Form.Check
                                                type="switch"
                                                className="filter-switch"
                                                id="favorites-switch"
                                                checked={selectedFavorites === true}
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={(e) =>
                                                    setSelectedFavorites?.(e.target.checked ? true : null)
                                                }
                                                aria-label="Saved to Favorites"
                                            />
                                        </label>
                                    </div>
                                </Col>
                            </Row>
                        )}
                        </>
                    ) : (
                        <>
                            <Row className="g-3 align-items-end flex-nowrap">
                                {!hideSearchField && (
                                    <Col md={6} lg>
                                        <Form.Group controlId="discoverySearch">
                                            <Form.Label>{searchLabel}</Form.Label>
                                            <InputGroup>
                                                <Form.Control
                                                    type="text"
                                                    placeholder={searchPlaceholder}
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery?.(e.target.value)}
                                                />
                                                <InputGroup.Text>
                                                    <img src={icSearch} alt="Search" />
                                                </InputGroup.Text>
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>
                                )}

                                {showCountryFilter && (
                                    <Col md={6} lg>
                                        <Form.Group controlId="discoveryCountry">
                                            <Form.Label>{countryLabel}</Form.Label>
                                            <Form.Select
                                                value={selectedCountry}
                                                onChange={(e) => {
                                                    setSelectedCountry?.(e.target.value);
                                                    setSelectedCity?.('');
                                                }}
                                            >
                                                <option value="">{countryPlaceholder}</option>
                                                {uniqueCountries.map((country) => (
                                                    <option key={country.value} value={country.value}>
                                                        {country.label}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                )}

                                {showCityFilter && (
                                    <Col md={6} lg>
                                        <Form.Group controlId="discoveryCity">
                                            <Form.Label>{cityLabel}</Form.Label>
                                            <Form.Select
                                                value={selectedCity}
                                                onChange={(e) => setSelectedCity?.(e.target.value)}
                                            >
                                                <option value="">{cityPlaceholder}</option>
                                                {uniqueCities.map((city, index) => (
                                                    <option key={index} value={city}>
                                                        {city}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                )}

                                <Col md={6} lg>
                                    <Form.Group controlId="discoveryIndustry">
                                        <Form.Label>{industryLabel}</Form.Label>
                                        <Form.Select
                                            value={selectedIndustry}
                                            onChange={(e) => setSelectedIndustry?.(e.target.value)}
                                        >
                                            <option value="">{industryPlaceholder}</option>
                                            {uniqueIndustries.map((industry, index) => (
                                                <option key={index} value={industry.value}>
                                                    {industry.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                <Col md={6} lg>
                                    <Form.Group controlId="discoveryStudyDirection">
                                        <Form.Label>Study Direction</Form.Label>
                                        <Form.Select
                                            value={selectedStudyDirection}
                                            onChange={(e) => setSelectedStudyDirection?.(e.target.value)}
                                        >
                                            <option value="">Filter by study direction...</option>
                                            {uniqueStudyDirections.map((direction, index) => (
                                                <option key={index} value={direction}>
                                                    {direction}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                <Col md={6} lg>
                                    <Form.Group controlId="discoveryCompanySize">
                                        <Form.Label>Company size</Form.Label>
                                        <Form.Select
                                            value={selectedCompanySize}
                                            onChange={(e) => setSelectedCompanySize?.(e.target.value)}
                                        >
                                            <option value="">Please select</option>
                                            {companySizeOptions.map((size, index) => (
                                                <option key={index} value={size}>
                                                    {size}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                <Col md={6} lg>
                                    <Form.Group controlId="discoveryCompanyRating">
                                        <Form.Label>Company rating</Form.Label>
                                        <Form.Select
                                            value={selectedCompanyRating}
                                            onChange={(e) => setSelectedCompanyRating?.(e.target.value)}
                                        >
                                            <option value="">Please select</option>
                                            {companyRatingOptions.map((rating) => (
                                                <option key={rating.value} value={rating.value}>
                                                    {rating.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                {!mobileModalOnly && (
                                    <Col xs="auto" className="d-flex align-items-end discovery-filter-save-col">
                                        <Button
                                            variant="primary"
                                            className="btn-sm discovery-filter-save-btn"
                                            onClick={() => setShowSaveFilterModal(true)}
                                        >
                                            Save Filters
                                        </Button>
                                    </Col>
                                )}
                            </Row>

                            <Row className="mt-3 discovery-filter-switch-row-group">
                                <Col xs={12} className="discovery-filter-switch-col">
                                    <div className="d-flex flex-wrap gap-3 justify-content-start discovery-filter-switches">
                                        {mobileModalOnly ? (
                                            <>
                                                <label className="discovery-filter-mobile-switch-row" htmlFor="english-speaking-switch">
                                                    <span>EN speaking companies</span>
                                                    <Form.Check
                                                        type="switch"
                                                        className="filter-switch"
                                                        id="english-speaking-switch"
                                                        checked={selectedEnglishSpeaking === true}
                                                        onClick={(e) => e.stopPropagation()}
                                                        onChange={(e) =>
                                                            setSelectedEnglishSpeaking?.(e.target.checked ? true : null)
                                                        }
                                                        aria-label="EN speaking companies"
                                                    />
                                                </label>

                                                <label className="discovery-filter-mobile-switch-row" htmlFor="world-traveler-switch">
                                                    <span>World traveler companies</span>
                                                    <Form.Check
                                                        type="switch"
                                                        className="filter-switch"
                                                        id="world-traveler-switch"
                                                        checked={selectedWorldTraveler === true}
                                                        onClick={(e) => e.stopPropagation()}
                                                        onChange={(e) =>
                                                            setSelectedWorldTraveler?.(e.target.checked ? true : null)
                                                        }
                                                        aria-label="World traveler companies"
                                                    />
                                                </label>

                                                <label className="discovery-filter-mobile-switch-row" htmlFor="student-job-areas-switch">
                                                    <span>Student job areas</span>
                                                    <Form.Check
                                                        type="switch"
                                                        className="filter-switch"
                                                        id="student-job-areas-switch"
                                                        checked={selectedStudentJobAreas === true}
                                                        onClick={(e) => e.stopPropagation()}
                                                        onChange={(e) =>
                                                            setSelectedStudentJobAreas?.(e.target.checked ? true : null)
                                                        }
                                                        aria-label="Student job areas"
                                                    />
                                                </label>

                                                <label className="discovery-filter-mobile-switch-row" htmlFor="favorites-switch">
                                                    <span>Saved to Favorites</span>
                                                    <Form.Check
                                                        type="switch"
                                                        className="filter-switch"
                                                        id="favorites-switch"
                                                        checked={selectedFavorites === true}
                                                        onClick={(e) => e.stopPropagation()}
                                                        onChange={(e) =>
                                                            setSelectedFavorites?.(e.target.checked ? true : null)
                                                        }
                                                        aria-label="Saved to Favorites"
                                                    />
                                                </label>
                                            </>
                                        ) : (
                                            <>
                                                <Form.Check
                                                    type="switch"
                                                    className="filter-switch"
                                                    id="english-speaking-switch"
                                                    label="EN speaking companies"
                                                    checked={selectedEnglishSpeaking === true}
                                                    onChange={(e) =>
                                                        setSelectedEnglishSpeaking?.(e.target.checked ? true : null)
                                                    }
                                                />

                                                <Form.Check
                                                    type="switch"
                                                    className="filter-switch"
                                                    id="world-traveler-switch"
                                                    label="World traveler companies"
                                                    checked={selectedWorldTraveler === true}
                                                    onChange={(e) =>
                                                        setSelectedWorldTraveler?.(e.target.checked ? true : null)
                                                    }
                                                />

                                                <Form.Check
                                                    type="switch"
                                                    className="filter-switch"
                                                    id="student-job-areas-switch"
                                                    label="Student job areas"
                                                    checked={selectedStudentJobAreas === true}
                                                    onChange={(e) =>
                                                        setSelectedStudentJobAreas?.(e.target.checked ? true : null)
                                                    }
                                                />

                                                <Form.Check
                                                    type="switch"
                                                    className="filter-switch"
                                                    id="favorites-switch"
                                                    label="Saved to Favorites"
                                                    checked={selectedFavorites === true}
                                                    onChange={(e) =>
                                                        setSelectedFavorites?.(e.target.checked ? true : null)
                                                    }
                                                />
                                            </>
                                        )}
                                    </div>
                                </Col>
                            </Row>
                        </>
                    )}

                    {extraFilters && (
                        <div className="mt-3">
                            {extraFilters}
                        </div>
                    )}
                </div>

                {showActiveFilterSummary && (
                    <Row className="filter-results">
                        <Col className="d-flex align-items-center flex-wrap gap-2">
                            <span>Filters:</span>

                            {searchQuery && (
                                <Badge bg="info" className="me-1">
                                    Search: {searchQuery}{' '}
                                    <Button
                                        variant="link"
                                        size="sm"
                                        onClick={() => setSearchQuery?.('')}
                                    >
                                        ×
                                    </Button>
                                </Badge>
                            )}

                            {selectedCountry && (
                                <Badge bg="info" className="me-1">
                                    {countryNameByCode.get(String(selectedCountry).trim().toUpperCase()) || selectedCountry}{' '}
                                    <Button
                                        variant="link"
                                        size="sm"
                                        onClick={() => {
                                            setSelectedCountry?.('');
                                            setSelectedCity?.('');
                                        }}
                                    >
                                        ×
                                    </Button>
                                </Badge>
                            )}

                            {selectedCity && (
                                <Badge bg="info" className="me-1">
                                    {selectedCity}{' '}
                                    <Button
                                        variant="link"
                                        size="sm"
                                        onClick={() => setSelectedCity?.('')}
                                    >
                                        ×
                                    </Button>
                                </Badge>
                            )}

                            {selectedIndustry && (
                                <Badge bg="info" className="me-1">
                                    Industry: {
                                        (() => {
                                            const label = industryNameByCode.get(String(selectedIndustry)) || String(selectedIndustry);
                                            return /^\d+(?:\.\d+)?$/.test(String(label).trim())
                                                ? 'Selected industry'
                                                : label;
                                        })()
                                    }{' '}
                                    <Button
                                        variant="link"
                                        size="sm"
                                        onClick={() => setSelectedIndustry?.('')}
                                    >
                                        ×
                                    </Button>
                                </Badge>
                            )}

                            {aiIndustryGroupLabel && (
                                <Badge bg="info" className="me-1">
                                    Industry: {aiIndustryGroupLabel}{' '}
                                    <Button
                                        variant="link"
                                        size="sm"
                                        onClick={() => onClearAiIndustryGroup?.()}
                                    >
                                        ×
                                    </Button>
                                </Badge>
                            )}

                            {selectedStudyDirection && (
                                <Badge bg="info" className="me-1">
                                    Study: {selectedStudyDirection}{' '}
                                    <Button
                                        variant="link"
                                        size="sm"
                                        onClick={() => setSelectedStudyDirection?.('')}
                                    >
                                        ×
                                    </Button>
                                </Badge>
                            )}

                            {selectedCompanySize && (
                                <Badge bg="info" className="me-1">
                                    Size: {selectedCompanySize}{' '}
                                    <Button
                                        variant="link"
                                        size="sm"
                                        onClick={() => setSelectedCompanySize?.('')}
                                    >
                                        ×
                                    </Button>
                                </Badge>
                            )}

                            {selectedCompanyRating && (
                                <Badge bg="info" className="me-1">
                                    Rating: {selectedCompanyRating}+{' '}
                                    <Button
                                        variant="link"
                                        size="sm"
                                        onClick={() => setSelectedCompanyRating?.('')}
                                    >
                                        ×
                                    </Button>
                                </Badge>
                            )}

                            {selectedEnglishSpeaking === true && (
                                <Badge bg="info" className="me-1">
                                    EN speaking companies{' '}
                                    <Button
                                        variant="link"
                                        size="sm"
                                        onClick={() => setSelectedEnglishSpeaking?.(null)}
                                    >
                                        ×
                                    </Button>
                                </Badge>
                            )}

                            {selectedWorldTraveler === true && (
                                <Badge bg="info" className="me-1">
                                    World traveler companies{' '}
                                    <Button
                                        variant="link"
                                        size="sm"
                                        onClick={() => setSelectedWorldTraveler?.(null)}
                                    >
                                        ×
                                    </Button>
                                </Badge>
                            )}

                            {selectedStudentJobAreas === true && (
                                <Badge bg="info" className="me-1">
                                    Student job areas{' '}
                                    <Button
                                        variant="link"
                                        size="sm"
                                        onClick={() => setSelectedStudentJobAreas?.(null)}
                                    >
                                        ×
                                    </Button>
                                </Badge>
                            )}

                            {selectedContinentAllocation && (
                                <Badge bg="info" className="me-1">
                                    Continent allocation: {selectedContinentAllocation}{' '}
                                    <Button
                                        variant="link"
                                        size="sm"
                                        onClick={() => setSelectedContinentAllocation?.('')}
                                    >
                                        ×
                                    </Button>
                                </Badge>
                            )}

                            {Array.isArray(selectedLanguagesSpoken) && selectedLanguagesSpoken.length > 0 && (
                                <Badge bg="info" className="me-1">
                                    Languages spoken: {selectedLanguagesSpoken.join(', ')}{' '}
                                    <Button
                                        variant="link"
                                        size="sm"
                                        onClick={() => setSelectedLanguagesSpoken?.([])}
                                    >
                                        ×
                                    </Button>
                                </Badge>
                            )}

                            {selectedFavorites === true && (
                                <Badge bg="info" className="me-1">
                                    Favorites{' '}
                                    <Button
                                        variant="link"
                                        size="sm"
                                        onClick={() => setSelectedFavorites?.(null)}
                                    >
                                        ×
                                    </Button>
                                </Badge>
                            )}

                            {/* Clear All Filters Button */}
                            <Button variant="link" size="sm" className="ms-auto" onClick={clearFilters}>
                                Clear All
                            </Button>
                        </Col>
                    </Row>
                )}
            </div>
    );

    return (
        <>
            {!mobileModalOnly && filterBarMarkup}

            {mobileModalOnly && (
                <Modal
                    className={`with-form discovery-filter-mobile-modal${useFullScreenMobilePanel ? ' discovery-filter-mobile-modal--fullscreen' : ''}`}
                    show={mobileModalOpen}
                    onHide={onMobileModalClose}
                >
                    <div className="discovery-filter-mobile-panel">
                        <div className="discovery-filter-mobile-panel__header">
                            <Modal.Title>{mobileModalTitle}</Modal.Title>

                            <div className="discovery-filter-mobile-panel__header-actions">
                                <Button
                                    type="button"
                                    variant="link"
                                    className="discovery-filter-mobile-panel__clear"
                                    onClick={clearFilters}
                                >
                                    Clear all
                                </Button>

                                <button
                                    type="button"
                                    className="discovery-filter-mobile-panel__close"
                                    onClick={onMobileModalClose}
                                    aria-label="Close filters"
                                >
                                    &times;
                                </button>
                            </div>
                        </div>

                        <Modal.Body>
                            {filterBarMarkup}
                        </Modal.Body>

                        <div className="discovery-filter-mobile-panel__footer">
                            <Button
                                type="button"
                                variant="outline-secondary"
                                className="discovery-filter-mobile-panel__reset"
                                onClick={clearFilters}
                            >
                                Clear all
                            </Button>

                            <Button
                                type="button"
                                variant="primary"
                                className="discovery-filter-mobile-panel__apply"
                                onClick={onMobileModalApply || onMobileModalClose}
                            >
                                {mobileApplyLabel}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}
            
            {/* Save Filter Modal */}
            <Modal
                className={`with-form${mobileModalOnly ? ' discovery-filter-nested-modal' : ''}`}
                backdropClassName={mobileModalOnly ? 'discovery-filter-nested-modal-backdrop' : undefined}
                show={showSaveFilterModal}
                onHide={() => {
                    setShowSaveFilterModal(false);
                    setFilterSetName('');
                }}
                centered
            >
                <Form
                    onSubmit={async (e) => {
                        e.preventDefault();

                        const trimmedName = filterSetName.trim();
                        if (!trimmedName) return;

                        await onSaveFilters?.(trimmedName);

                        setShowSaveFilterModal(false);
                        setFilterSetName('');
                    }}
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Save filter set as</Modal.Title>
                    </Modal.Header>

                    <Modal.Body>
                        <p>This will create a new set of filters on the Discovery page.</p>

                        <Form.Group className="mb-0">
                            <Form.Label>Filter set name</Form.Label>
                            <Form.Control
                                type="text"
                                value={filterSetName}
                                onChange={(e) => setFilterSetName(e.target.value)}
                                placeholder="Enter filter set name"
                            />
                        </Form.Group>
                    </Modal.Body>

                    <Modal.Footer className="discovery-filter-set-modal__footer discovery-filter-set-modal__footer--save">
                        <Button
                            variant="outline-secondary"
                            onClick={() => {
                                setShowSaveFilterModal(false);
                                setFilterSetName('');
                            }}
                        >
                            Cancel
                        </Button>

                        <Button
                            variant="primary"
                            className="ms-3"
                            type="submit"
                            disabled={!filterSetName.trim()}
                        >
                            Save
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Delete Filter Modal */}
            <Modal
                className={`with-form${mobileModalOnly ? ' discovery-filter-nested-modal' : ''}`}
                backdropClassName={mobileModalOnly ? 'discovery-filter-nested-modal-backdrop' : undefined}
                show={showDeleteFilterModal}
                onHide={() => {
                    setShowDeleteFilterModal(false);
                    setFilterSetToDelete(null);
                }}
                centered
            >
                <Form
                    onSubmit={async (e) => {
                        e.preventDefault();

                        if (!filterSetToDelete?._id && !filterSetToDelete?.value) return;

                        try {
                            await onDeleteFilterSet?.(filterSetToDelete._id || filterSetToDelete.value);

                            setShowDeleteFilterModal(false);
                            setFilterSetToDelete(null);
                        } catch (err) {
                            console.error('Error deleting filter set:', err);
                        }
                    }}
                >
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <Modal.Title>
                                Are you sure you want to delete “{filterSetToDelete?.name || filterSetToDelete?.label}”?
                            </Modal.Title>
                        </Modal.Title>
                    </Modal.Header>

                    <Modal.Body>
                        <p>Deleting this filter set will remove all its saved criteria.</p>
                    </Modal.Body>

                    <Modal.Footer className="discovery-filter-set-modal__footer discovery-filter-set-modal__footer--delete">
                        <Button
                            variant="outline-secondary"
                            onClick={() => {
                                setShowDeleteFilterModal(false);
                                setFilterSetToDelete(null);
                            }}
                        >
                            No, keep this filter set
                        </Button>

                        <Button
                            variant="primary"
                            className="ms-3"
                            type="submit"
                        >
                            Yes, delete this filter set
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </>
    );
};

export default DiscoveryFilterBar;
